import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { PixiSettings } from "../../../../pixiSettings";
import { getAssetsPath } from "../../../../utils/pathConfig";
import TickerTimeout from "../../../../utils/TickerTimeout";
import {
  getLoadingSpineTargetHeight,
  measureLoadingSpineBounds,
} from "../../shared/Popups/IntroPopup/IntroPopup/IntroPopupComposedController";

// Black background for loader
const LOADER_BACKGROUND_COLOR = 0x000000;

// ============================================================================
// PRELOADER ARTWORK
//
// Three static images painted UNDER the preloader spine: a full-bleed scene,
// a glow, and the game logo. They are force-loaded by this controller rather
// than declared in assets.js — that bundle only finishes long after this
// screen is already on show, which would leave the loader blank until the
// game was practically loaded. Fetched the same way the spine is fetched, in
// the same tick, so the whole composition appears at once.
//
// Everything is sized as a ratio of the BACKGROUND, the way
// IntroPopupComposedController sizes its content off the intro background.
// Change the background and the glow and logo follow automatically.
// ============================================================================
// Cleopatra's Fire paints the LOGO only — no background, no glow (Vladimir,
// 2026-09-29: "use just logo and thats it").
const PRELOADER_IMAGES = {
  // BACKGROUND: "preloader_background",
  // GLOW: "logo_glow",
  // LOGO: "logo",
  // The loader's own logo art (Vladimir, 2026-10-02) — images/loader_logo.webp.
  LOGO: "loader_logo",
};

// The frame the logo and the wordmark are laid out in — the old background's
// native size, kept as an invisible reference so the composition keeps the
// proportions it was tuned with now that nothing is painted behind the logo.
const PRELOADER_FRAME = { WIDTH: 1380, HEIGHT: 761 };

// Loaded here but NOT painted. getLoadingSpineTargetHeight sizes the wordmark
// from the intro background's fitted width, so the loader and the intro show
// it at exactly the same size — and that means reading the texture out of the
// cache. It is declared in assets.js, but that bundle only finishes long after
// this screen is up, so asking for it here warned and fell back to guessed
// dimensions. Force-loaded with the rest, in the same tick.
const PRELOADER_SIZING_IMAGES = ["intro_background_landscape"];

const PRELOADER_LAYOUT = {
  // Background only — nudges it sideways as a fraction of its own width.
  // Positive is right. The logo, glow and spine stay on the screen centre.
  bgXOffsetRatio: 0.02,
  // Logo width as a fraction of the rendered background width.
  // logoWRatio: 0.375,
  // 0.5 (2026-10-05): 0.375 was Mad Labs' (a 534x320 logo with no margin);
  // loader_logo has ~25% transparent margin around its art, so the same
  // ratio drew it a quarter smaller.
  logoWRatio: 0.5,
  // Logo centre, as a fraction of background height from the centre.
  // logoYRatio: -0.093,
  // -0.03 (2026-10-05): -0.093 lifted Mad Labs' short, wide logo; this one is
  // nearly square and sat well above the middle.
  logoYRatio: -0.03,
  // The glow is sized off the LOGO, not the background — the two read as one
  // object, so they must scale together. Same pair of numbers as the intro
  // popup's PORTRAIT_CFG / LANDSCAPE_CFG, so the glow sits identically
  // relative to the logo on both screens.
  glowScaleVsLogo: 1.6,
  glowYOffsetVsLogo: 0.08,
};

// The wordmark's bone sits at y = -364.43, nowhere near the skeleton root, so
// positioning the spine by its origin drops the letters far off target. Pose
// the animation, measure what is actually there, and centre THAT instead.
// Sampled mid-animation because each letter fades in on its own schedule (the
// last at 0.7667s) — measuring at t = 0 would catch a half-formed word.
const SPINE_POSE_TIME = 1.2;

// Painted below the spine (zIndex 10), which is left exactly as it was.
const PRELOADER_Z = {
  BACKGROUND: 1,
  GLOW: 2,
  LOGO: 3,
};

// ── How long the loader may stay blank waiting to be placed properly ──
//
// Every pass that cannot measure the background asks for another one. This is
// the point at which it stops asking and shows the composition anyway, placed
// against the viewport instead of the artwork. Without it, a background image
// that never arrives would leave the player on a black screen.
//
// Long enough that a slow first load still gets its proper layout, short
// enough that a real failure is not a wait.
const LAYOUT_RETRY_DEADLINE_MS = 2500;

// How often a failed pass tries again. Frame-rate independent, and far cheaper
// than every frame — nothing it waits for resolves faster than this.
const LAYOUT_RETRY_INTERVAL_MS = 100;

// Safety cap on waiting for the preloader spine to finish its revolution.
// Only trips if the spine's complete event never arrives — without it a
// missing event would leave the player stuck on the loader forever.
const SPINE_COMPLETE_MAX_WAIT_MS = 4000;

// ============================================================================
// PER-DEVICE BACKGROUND SIZE
// Same shape as this file's ERROR_POPUP_CONFIG.DEVICE_CONFIGS and the
// DEVICE_MULTIPLIERS tables elsewhere in the codebase.
//
// bgScaleMult: multiplier on top of the fitted background scale. The fit is
// width-driven, so this is effectively "how much of the screen width the
// background takes". The logo and glow are ratios of the background, so this
// one number per device scales the composition.
//
// spineYRatio: where the PopiPlay wordmark sits, as a fraction of the
// background height BELOW the background centre. 0.5 is the background's
// bottom edge, so anything above that is inside it and anything below is
// clear of it. Portrait pushes the wordmark well clear; landscape keeps it
// tight to the edge. POSITION only — the wordmark's SIZE comes from the intro
// popup's getLoadingSpineTargetHeight so the two screens match exactly.
//
// logoScaleMult: the logo's size on top of logoWRatio, per device (1 unless
// set) — the logo only; the wordmark and the frame do not move.
//
// The old `scale` / `offset` fields are gone: they sized and centred the
// spine, which now takes its size from the intro popup and its position from
// the background. Nothing read them any more.
// ============================================================================

const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = {
  bgScaleMult: 1.3,
  spineYRatio: 0.55,
  // The logo alone bigger here (2026-10-05: "logo too small on mobile
  // portrait"); the wordmark stays where it is. 1 when unset.
  logoScaleMult: 1.35,
};

const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = {
  bgScaleMult: 0.7,
  spineYRatio: 0.475,
  logoScaleMult: 1.2, // the logo alone bigger (2026-10-05)
};

const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = {
  bgScaleMult: 0.8,
  spineYRatio: 0.75,
};

const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = {
  bgScaleMult: 0.7,
  spineYRatio: 0.475,
};

const DEVICE_MULTIPLIERS_DESKTOP = {
  bgScaleMult: 0.7,
  // spineYRatio: 0.475,
  // 0.35 (2026-10-05): the wordmark closer under the (bigger, lower) logo —
  // ~80 px up on a 1728x939 desktop.
  spineYRatio: 0.35,
};

/**
 * LoaderController — Handles the secondary preloader screen.
 *
 * Uses a single "preloader" spine that contains both the logo and loader
 * animation. The spine is centered on screen with device-specific scale
 * and Y offset (same pattern as the primary LoaderController).
 *
 * Flow: load spine → play "popiplay_logo" (looping) → onLoadingComplete()
 *       → wait for current loop to finish → resolve → Loader.jsx fades out.
 */
class LoaderController {
  constructor(container, dimensions, symbolHeight, dimensionsContext) {
    this.container = container;
    this.dimensions = dimensions;
    this.symbolHeight = symbolHeight;
    this.dimensionsContext = dimensionsContext;

    // Visual elements
    this.preloaderSpine = null;
    // True once the artwork load has finished, succeeded or not. The spine
    // stays hidden until then — see _layoutArtwork.
    this._artworkSettled = false;
    this._bgSprite = null;
    this._glowSprite = null;
    this._logoSprite = null;
    this._spineBounds = null;
    // Raised together, once, by _reveal — see the note there. Until then every
    // piece of this screen is built and posed but transparent.
    this._revealed = false;
    this._layoutRetry = null;
    this._layoutDeadlineAt = 0;

    // State
    this.isPlaying = false;
    this.initialized = false;
    this.errorOccurred = false;

    // Loading lifecycle — the hand-off waits for the spine's CURRENT
    // revolution to finish, then holds its final frame (see onLoadingComplete).
    this._completeTimeout = null;

    // Clickable fullscreen background (mirrors IntroPopupComposedController)
    this._backgroundGraphics = null;
    this._backgroundClickHandler = null;

    // Caches
    this._availableAnimationsCache = null;
    this._deviceMultipliersCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    // Full-screen clickable background — tap anywhere to enter fullscreen
    this._createClickableBackground();

    // Load spine and start animation
    this.initialize();
  }

  // =========================================================================
  // Clickable background — fullscreen on tap (IntroPopup pattern)
  // =========================================================================

  _createClickableBackground() {
    this._backgroundGraphics = new PIXI.Graphics();
    this._backgroundGraphics.zIndex = 0;
    this._backgroundGraphics.eventMode = "static";
    this._backgroundGraphics.cursor = "default";
    this._backgroundGraphics.on("pointertap", (e) => {
      if (this._backgroundClickHandler) this._backgroundClickHandler(e);
    });
    this.container.addChild(this._backgroundGraphics);
    this.container.alpha = 1;
    this._updateBackgroundDimensions();
  }

  _updateBackgroundDimensions() {
    if (!this._backgroundGraphics || !this.dimensions) return;

    const { width, height } = this.dimensions;
    this._backgroundGraphics.clear();
    this._backgroundGraphics.beginFill(LOADER_BACKGROUND_COLOR);
    this._backgroundGraphics.drawRect(0, 0, width, height);
    this._backgroundGraphics.endFill();
  }

  setBackgroundClickHandler(handler) {
    this._backgroundClickHandler = handler;
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  async initialize() {
    // Started here, in the same tick as the spine, but deliberately NOT
    // awaited and deliberately outside the try/catch below.
    //
    // The artwork must never be able to take the spine down with it. When it
    // was awaited inside the same block, one failure in the image path
    // skipped the rest of this method and left the loader completely blank —
    // no spine, no artwork, nothing.
    this._startArtwork();

    try {
      const BASE_PATH = getAssetsPath();
      const jsonPath = `${BASE_PATH}/spines/preloader/preloader.json`;
      const atlasPath = `${BASE_PATH}/spines/preloader/preloader.atlas`;
      const skeletonAlias = "preloader-skeleton";
      const atlasAlias = "preloader-atlas";

      if (!PIXI.Assets.cache.has(skeletonAlias)) {
        PIXI.Assets.add({ alias: skeletonAlias, src: jsonPath });
        PIXI.Assets.add({ alias: atlasAlias, src: atlasPath });
        await PIXI.Assets.load([skeletonAlias, atlasAlias]);
      }

      // If error occurred while loading, don't create spine
      if (this.errorOccurred) return;

      this.preloaderSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!this.preloaderSpine?.skeleton?.data?.animations) return;

      this._availableAnimationsCache = null;

      this.container.addChild(this.preloaderSpine);
      // ── Invisible until it has a real layout ──
      //
      // Everything on this screen is measured off the background image: the
      // logo is a ratio of its rendered width, and the spine hangs off the
      // logo. The image is loaded fire-and-forget, so the spine is almost
      // always ready first — and laying out then falls back to the raw
      // viewport, which puts the wordmark somewhere arbitrary. The images
      // land a moment later, _layoutArtwork runs again, and the whole thing
      // jumps into place.
      //
      // So it is built and posed but not shown. _layoutArtwork raises this the
      // first time it runs with a background to measure. Alpha rather than
      // `visible`, as everywhere else here — an invisible subtree is skipped
      // for transforms too, and the spine has to keep advancing its track.
      this.preloaderSpine.alpha = 0;
      this.preloaderSpine.zIndex = 10;
      this.preloaderSpine.eventMode = "none";
      this.initialized = true;

      // Animation FIRST: the layout measures the spine's posed bounds, and
      // that measurement needs a track to advance. Measuring before the
      // animation is set would capture the setup pose instead.
      this.playAnimation();
      this.applyScaleAndCenter();
    } catch (error) {
      // Preloader continues without animation, but never silently — a blank
      // loading screen is impossible to diagnose from a screenshot.
      console.error("[LoaderController] preloader spine failed:", error);
    }
  }

  /**
   * Fetch the artwork and attach it when it lands.
   *
   * Fire-and-forget on purpose. The spine does not wait for these images, and
   * nothing thrown in here can reach the spine's own initialization.
   */
  _startArtwork() {
    this._loadPreloaderImages(getAssetsPath())
      .then(() => {
        if (this.errorOccurred || !this.container) return;
        this._createArtwork();
        // Set BEFORE the layout: the artwork pass is over, so whatever the
        // layout produces now is final, whether or not every image arrived.
        this._artworkSettled = true;
        this._layoutArtwork();
      })
      .catch((error) => {
        console.error("[LoaderController] preloader artwork failed:", error);
        // The artwork is gone, so no pass will ever measure a background.
        // Rather than raising the spine where it stands — the skeleton origin,
        // i.e. the corner — mark the wait over and force one placement against
        // the viewport. Badly placed beats a screen that never appears; the
        // corner is neither.
        this._artworkSettled = true;
        this._layoutDeadlineAt = performance.now();
        this._layoutArtwork();
      });
  }

  // =========================================================================
  // Preloader artwork — background / glow / logo
  // =========================================================================

  /**
   * Force-load the preloader images — the three that are painted, plus the
   * intro background the wordmark is sized from.
   *
   * Cache-guarded on both the cache AND the resolver: "logo" and the intro
   * background are also declared in assets.js, and adding an alias the
   * resolver already knows logs a warning. A failed image must never block the
   * loader, so each one swallows its own error and the composition simply
   * renders without it.
   */
  async _loadPreloaderImages(basePath) {
    const aliases = [
      ...Object.values(PRELOADER_IMAGES),
      ...PRELOADER_SIZING_IMAGES,
    ];
    await Promise.all(
      aliases.map(async (alias) => {
        try {
          if (PIXI.Assets.cache.has(alias)) return;
          if (!PIXI.Assets.resolver?.hasKey?.(alias)) {
            PIXI.Assets.add({ alias, src: `${basePath}/images/${alias}.webp` });
          }
          await PIXI.Assets.load(alias);
        } catch {
          // Missing image — degrade to whatever did load.
        }
      }),
    );
  }

  _createArtwork() {
    const make = (alias, zIndex) => {
      if (!PIXI.Assets.cache.has(alias)) return null;
      const texture = PIXI.Assets.get(alias);
      if (!texture) return null;
      const sprite = new PIXI.Sprite(texture);
      sprite.anchor.set(0.5);
      // Transparent until placed. A sprite is created at 0,0 at its native
      // size, and anchoring it centres that on the screen's TOP-LEFT corner —
      // so a pass that bails before positioning it does not leave it unplaced,
      // it leaves it in the corner. See _layoutArtwork.
      sprite.alpha = 0;
      sprite.zIndex = zIndex;
      sprite.eventMode = "none";
      this.container.addChild(sprite);
      return sprite;
    };

    // this._bgSprite = make(PRELOADER_IMAGES.BACKGROUND, PRELOADER_Z.BACKGROUND);
    // this._glowSprite = make(PRELOADER_IMAGES.GLOW, PRELOADER_Z.GLOW);
    this._logoSprite = make(PRELOADER_IMAGES.LOGO, PRELOADER_Z.LOGO);
  }

  /**
   * Lay out background → glow → logo around the centre of the screen.
   *
   * The background is the reference frame: it covers the viewport, and the
   * glow and logo are ratios of its rendered size. Widths are set from the
   * ratio and heights follow the texture's own aspect, so nothing distorts.
   */
  _layoutArtwork() {
    const width = this.dimensions?.width ?? 0;
    const height = this.dimensions?.height ?? 0;
    // ── A zero-size viewport is not a small one ──
    //
    // The layout emits 0x0 on its first pass, before the canvas has been
    // measured. Every number on this screen is a ratio of that, so a pass
    // taken on it computes zeroes and leaves the composition on the origin.
    // Ask for another frame instead of laying out against nothing.
    if (width <= 0 || height <= 0) {
      this._scheduleLayoutRetry();
      return;
    }
    const centreX = width / 2;
    const centreY = height / 2;

    // let bgW = width;
    // let bgH = height;
    // The invisible frame (PRELOADER_FRAME), fitted exactly as the painted
    // background was — the logo and the wordmark are ratios of it.
    const frameScale =
      Math.min(width / PRELOADER_FRAME.WIDTH, height / PRELOADER_FRAME.HEIGHT) *
      this.getDeviceMultipliers().bgScaleMult;
    let bgW = PRELOADER_FRAME.WIDTH * frameScale;
    let bgH = PRELOADER_FRAME.HEIGHT * frameScale;
    if (this._bgSprite?.texture) {
      const nativeW = this._bgSprite.texture.width || width;
      const nativeH = this._bgSprite.texture.height || height;
      // FIT, not cover — min of the two ratios, so the whole background is
      // always inside the viewport. Cover scaled a tall portrait screen by
      // height and ran the width far past the edge, and since the logo, glow
      // and wordmark are all ratios of this, the entire composition blew up.
      const scale =
        Math.min(width / nativeW, height / nativeH) *
        this.getDeviceMultipliers().bgScaleMult;
      bgW = nativeW * scale;
      bgH = nativeH * scale;
      this._bgSprite.width = bgW;
      this._bgSprite.height = bgH;
      this._bgSprite.x = centreX + bgW * PRELOADER_LAYOUT.bgXOffsetRatio;
      this._bgSprite.y = centreY;
    }

    let logoW = 0;
    let logoH = 0;
    let logoY = centreY;
    if (this._logoSprite?.texture) {
      const tex = this._logoSprite.texture;
      const aspect = (tex.width || 1) / (tex.height || 1);
      // logoW = bgW * PRELOADER_LAYOUT.logoWRatio;
      logoW =
        bgW *
        PRELOADER_LAYOUT.logoWRatio *
        (this.getDeviceMultipliers().logoScaleMult ?? 1);
      logoH = logoW / aspect;
      logoY = centreY + bgH * PRELOADER_LAYOUT.logoYRatio;
      this._logoSprite.width = logoW;
      this._logoSprite.height = logoH;
      this._logoSprite.x = centreX;
      this._logoSprite.y = logoY;
    }

    if (this._glowSprite?.texture && logoW > 0) {
      const tex = this._glowSprite.texture;
      const aspect = (tex.width || 1) / (tex.height || 1);
      const glowW = logoW * PRELOADER_LAYOUT.glowScaleVsLogo;
      this._glowSprite.width = glowW;
      this._glowSprite.height = glowW / aspect;
      this._glowSprite.x = centreX;
      this._glowSprite.y = logoY + logoH * PRELOADER_LAYOUT.glowYOffsetVsLogo;
    }

    const placed = this._layoutSpine(
      centreX,
      centreY,
      bgH,
      this._layoutDeadlinePassed(),
    );

    // ── NOTHING is shown until it has been PLACED ──
    //
    // This is the guard the whole screen hangs on, so it is worth stating what
    // it prevents. Every piece here is measured off the background image: the
    // logo is a ratio of its rendered width, the glow a ratio of the logo, and
    // the wordmark hangs off both. A pass that cannot measure it — texture not
    // fetched yet, a viewport still reporting 0x0, spine bounds not yet
    // measurable — has not placed anything. It has left the defaults, and the
    // defaults are the skeleton origin and the sprite origin, which is the
    // screen's TOP-LEFT CORNER.
    //
    // Revealing on "the artwork pass finished" rather than on "the layout
    // worked" is what shipped that corner to production: the pass can finish
    // with a missing image, and the reveal then happened regardless.
    //
    // So a failed pass shows nothing and asks for another one. The deadline
    // above is the escape: after it, one placement against the viewport is
    // forced and the screen appears anyway.
    if (!placed) {
      this._scheduleLayoutRetry();
      return;
    }

    this._reveal();
  }

  /**
   * Whether the wait for a proper layout is over.
   *
   * The clock starts on the FIRST failed pass, not at construction, so a load
   * that never had to wait never starts one.
   */
  _layoutDeadlinePassed() {
    return (
      this._layoutDeadlineAt > 0 && performance.now() >= this._layoutDeadlineAt
    );
  }

  /**
   * Ask for another layout shortly.
   *
   * Self-healing rather than event-driven on purpose. A failed pass can be
   * waiting on any of four different things — the viewport, the background
   * texture, the intro background the size is derived from, or the spine's own
   * bounds — and each arrives on its own schedule with no single event to hang
   * off. Retrying covers all four without having to name them.
   *
   * updateDimensions cannot be that event: it has a jitter guard that ignores
   * changes under 50px, so the pass that finally has a real viewport may never
   * reach the layout at all.
   */
  _scheduleLayoutRetry() {
    if (this._revealed || this._layoutRetry) return;
    if (!this._layoutDeadlineAt) {
      this._layoutDeadlineAt = performance.now() + LAYOUT_RETRY_DEADLINE_MS;
    }
    this._layoutRetry = new TickerTimeout(() => {
      this._layoutRetry = null;
      if (this._revealed || this.errorOccurred || !this.container) return;
      this._layoutArtwork();
    }, LAYOUT_RETRY_INTERVAL_MS);
  }

  /**
   * Raise the whole composition, once, on the first pass that placed it.
   *
   * All four pieces together: three sprites and the spine. Fading them in
   * separately would show the background arrive before the logo it is sized
   * against, which is the same jump this guard exists to remove.
   *
   * Alpha rather than `visible`, as everywhere else here — an invisible
   * subtree is skipped for transforms too, and the spine has to keep
   * advancing its track while it waits.
   */
  _reveal() {
    this._revealed = true;
    if (this._layoutRetry) {
      this._layoutRetry.clear();
      this._layoutRetry = null;
    }
    for (const key of ["_bgSprite", "_glowSprite", "_logoSprite"]) {
      if (this[key]) this[key].alpha = 1;
    }
    // The spine still waits for the artwork pass to be over, so it cannot
    // appear alone in front of artwork that is still loading.
    if (this.preloaderSpine && this._artworkSettled) {
      this.preloaderSpine.alpha = 1;
    }
  }

  /**
   * Place the PopiPlay wordmark near the bottom of the background.
   *
   * Same approach as IntroPopupComposedController._layoutLoadingSpine: the
   * skeleton root is not where the letters are, so measure the posed bounds
   * once and position by the wordmark's own centre.
   *
   * The measurement is cached because this runs on every resize, and a FAILED
   * one is deliberately not cached: an empty box used to stick for the whole
   * session, and every later layout then bailed on it, leaving the wordmark
   * unscaled on the skeleton origin — far off screen, i.e. gone.
   */
  _layoutSpine(centreX, centreY, bgH, force = false) {
    const spine = this.preloaderSpine;
    if (!spine || bgH <= 0) return false;

    // ── Measured off the BACKGROUND, so refuse until there is one ──
    //
    // `bgH` above is the background's rendered height when the sprite has a
    // texture and the raw viewport height when it does not. The second is not a
    // worse answer, it is a different composition — the wordmark hangs off the
    // logo, which hangs off this. Placing it against the viewport and showing
    // it is what put it in the corner.
    // `force` is the deadline above having passed: there is no background and
    // there is not going to be one, so the viewport becomes the reference
    // frame. A different composition, not a wrong one.
    // if (!force && !this._bgSprite?.texture) return false;
    // No background any more — the frame is a constant. What the wordmark
    // hangs off now is the LOGO, so that is what is waited for.
    if (!force && !this._logoSprite?.texture) return false;

    if (!this._spineBounds) {
      this._spineBounds = measureLoadingSpineBounds(spine, SPINE_POSE_TIME);
    }

    const bounds = this._spineBounds;
    if (!bounds) return false; // nothing measurable yet — retry on the next layout

    // SIZE comes from the intro popup's shared calculation, so the wordmark is
    // pixel-identical on both screens for every device and orientation. Only
    // the POSITION below is ours.
    const targetHeight = getLoadingSpineTargetHeight({
      dimensions: this.dimensions,
      ...this.dimensionsContext,
    });
    if (!targetHeight) return false;

    const scale = targetHeight / bounds.height;
    spine.scale.set(scale);

    // Offset by the bounds' own centre, so the letters land on the target
    // rather than the skeleton origin landing on it.
    const boundsCX = bounds.x + bounds.width / 2;
    const boundsCY = bounds.y + bounds.height / 2;
    spine.x = centreX - boundsCX * scale;
    spine.y =
      centreY +
      bgH * this.getDeviceMultipliers().spineYRatio -
      boundsCY * scale;
    return true;
  }

  _destroyArtwork() {
    for (const key of ["_bgSprite", "_glowSprite", "_logoSprite"]) {
      const sprite = this[key];
      if (!sprite) continue;
      try {
        if (this.container?.children?.includes(sprite)) {
          this.container.removeChild(sprite);
        }
        // Textures are shared with the asset cache — destroy the sprite only.
        sprite.destroy();
      } catch {
        // Silent fail
      }
      this[key] = null;
    }
  }

  // =========================================================================
  // Positioning — same pattern as WinAnimationSecondaryController
  // =========================================================================

  /**
   * Get device/orientation-specific multipliers for the background.
   * Cached until device or orientation changes.
   */
  getDeviceMultipliers() {
    const { device, orientation } = this.dimensionsContext || {};

    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._deviceMultipliersCache;
    }

    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE || device === PixiSettings.DEVICE.MOBILE_SMALL) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        multipliers = DEVICE_MULTIPLIERS_TABLET_PORTRAIT;
      } else {
        multipliers = DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
      }
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    this._deviceMultipliersCache = multipliers;
    this._lastDevice = device;
    this._lastOrientation = orientation;
    return multipliers;
  }

  /**
   * Apply proportional scaling and centering to spine.
   * Same pattern as WinAnimationSecondaryController.applyScaleAndCenter:
   * base scale = symbolHeight / SYMBOL_HEIGHT, then × device multiplier.
   */
  applyScaleAndCenter() {
    // Background, glow, logo AND the spine are all laid out from the
    // background — see _layoutArtwork, which ends by calling _layoutSpine.
    //
    // The spine used to be scaled here from symbolHeight × a device
    // multiplier, and centred on the screen. Both are gone: the size now
    // comes from the intro popup's shared calculation so the two screens
    // match, and the position hangs off the logo.
    this._layoutArtwork();
  }

  // =========================================================================
  // Animation
  // =========================================================================

  /**
   * Returns array of available animation names from the spine.
   */
  getAvailableAnimations() {
    if (!this.preloaderSpine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache) return this._availableAnimationsCache;

    this._availableAnimationsCache = this.preloaderSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    return this._availableAnimationsCache;
  }

  /**
   * Starts playing the preloader animation loop.
   */
  playAnimation() {
    if (!this.preloaderSpine?.state || this.isPlaying) return;

    try {
      this.isPlaying = true;
      this.preloaderSpine.state.clearTracks();
      this.preloaderSpine.state.clearListeners();
      this.preloaderSpine.skeleton.setToSetupPose();

      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.preloaderSpine.state.setAnimation(0, animations[0], true);
      }
    } catch (error) {
      this.isPlaying = false;
    }
  }

  /**
   * Stop for good — init failed and LoadingErrorPopup has the screen.
   *
   * Sets errorOccurred before releasing, which matters because initialize()
   * and _startArtwork() are both still in flight at this point: they check the
   * flag and bail rather than attaching a spine or artwork to a loader that
   * has already been hidden, which would otherwise sit there animating,
   * unreachable, until unmount.
   */
  stop() {
    this.errorOccurred = true;
    this.releaseSpine();
    this._destroyArtwork();
  }

  /**
   * Free the spine but keep the controller alive.
   *
   * Called once the loader has faded out. The controller stays because the
   * component that owns it is still mounted and still tracks resizes; only
   * the spine is expensive enough to be worth releasing early.
   */
  releaseSpine() {
    this.isPlaying = false;
    if (!this.preloaderSpine) return;
    try {
      if (this.preloaderSpine.state) {
        this.preloaderSpine.state.clearListeners();
        this.preloaderSpine.state.clearTracks();
      }
      if (this.container?.children?.includes(this.preloaderSpine)) {
        this.container.removeChild(this.preloaderSpine);
      }
      this.preloaderSpine.destroy();
    } catch {
      // Silent fail
    }
    this.preloaderSpine = null;
  }

  /**
   * Hold the spine on its final frame — visible, but no longer moving.
   *
   * Deliberately NOT hidden: the loader's own fade-out carries it away a
   * moment later, so the animation reads as finishing and then dissolving
   * rather than snapping to invisible the instant its loop ends.
   */
  _holdFinalFrame() {
    this.isPlaying = false;
    if (!this.preloaderSpine?.state) return;
    try {
      this.preloaderSpine.state.timeScale = 0;
    } catch {
      // Silent fail — worst case it keeps looping until cleanup.
    }
  }

  // =========================================================================
  // Loading lifecycle
  // =========================================================================

  /**
   * How far the wordmark's current revolution has run, in seconds.
   *
   * Handed to the intro popup so its copy of the same spine carries on from
   * here instead of starting again — see setLoadingSpineHandoverTime.
   */
  getSpineTrackTime() {
    return this.preloaderSpine?.state?.tracks?.[0]?.trackTime ?? 0;
  }

  /**
   * Resolve once the spine has finished the revolution it is currently
   * playing, then hold it on its final frame.
   *
   * Previously this tracked "has any loop ever completed" and resolved
   * instantly if so — which meant a hand-off arriving mid-revolution chopped
   * the animation off. Waiting for the next complete event instead guarantees
   * the wordmark always finishes cleanly, and still guarantees at least one
   * full loop plays when loading is near-instant.
   */
  onLoadingComplete() {
    return new Promise((resolve) => {
      if (!this.initialized || !this.preloaderSpine?.state) {
        resolve();
        return;
      }

      const spine = this.preloaderSpine;
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        if (this._completeTimeout) {
          this._completeTimeout.clear();
          this._completeTimeout = null;
        }
        try {
          spine.state.removeListener(listener);
        } catch {
          // Silent fail
        }
        this._holdFinalFrame();
        resolve();
      };

      const listener = { complete: finish };
      spine.state.addListener(listener);
      this._completeTimeout = new TickerTimeout(
        finish,
        SPINE_COMPLETE_MAX_WAIT_MS,
      );
    });
  }


  // =========================================================================
  // Dimension updates
  // =========================================================================

  /**
   * Updates dimensions and re-positions the spine.
   */
  updateDimensions(dimensions, symbolHeight, dimensionsContext) {
    const deviceOrOrientationChanged =
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation ||
      this.dimensionsContext?.isMobileSmall !== dimensionsContext?.isMobileSmall;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;

    // Jitter guard: skip small dimension changes (e.g. mobile address bar)
    // Only reposition on significant size changes (>50px) or device/orientation change
    const widthDelta = Math.abs((this.dimensions?.width || 0) - (dimensions?.width || 0));
    const heightDelta = Math.abs((this.dimensions?.height || 0) - (dimensions?.height || 0));
    const significantSizeChange = widthDelta > 50 || heightDelta > 50;

    // Background always tracks viewport — needed for fullscreen hit area
    this.dimensions = dimensions;
    this._updateBackgroundDimensions();

    if (!deviceOrOrientationChanged && !symbolHeightChanged && !significantSizeChange) {
      return;
    }

    this.symbolHeight = symbolHeight;
    if (dimensionsContext) {
      if (deviceOrOrientationChanged) {
        this._deviceMultipliersCache = null;
      }
      this.dimensionsContext = dimensionsContext;
    }

    if (this.initialized && !this.errorOccurred) {
      this.applyScaleAndCenter();
    }

  }

  // =========================================================================
  // Cleanup
  // =========================================================================

  cleanup() {
    this.isPlaying = false;
    if (this._layoutRetry) {
      this._layoutRetry.clear();
      this._layoutRetry = null;
    }
    this._destroyArtwork();
    if (this._completeTimeout) {
      this._completeTimeout.clear();
      this._completeTimeout = null;
    }
    this._backgroundClickHandler = null;

    if (this._backgroundGraphics) {
      this._backgroundGraphics.removeAllListeners();
      if (this.container?.children?.includes(this._backgroundGraphics)) {
        this.container.removeChild(this._backgroundGraphics);
      }
      this._backgroundGraphics.destroy();
      this._backgroundGraphics = null;
    }

    // Clean up spine
    if (this.preloaderSpine) {
      try {
        if (this.preloaderSpine.state) {
          this.preloaderSpine.state.clearListeners();
          this.preloaderSpine.state.clearTracks();
          this.preloaderSpine.state.timeScale = 1.0;
        }
        if (this.container?.children.includes(this.preloaderSpine)) {
          this.container.removeChild(this.preloaderSpine);
        }
        this.preloaderSpine.destroy();
      } catch (error) {
        // ignore
      }
      this.preloaderSpine = null;
    }

    this.initialized = false;
    this._availableAnimationsCache = null;
  }
}

export default LoaderController;
